import React, { useEffect, useState, useContext } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import { OidcProvider, OidcSecure, useOidc } from '@axa-fr/react-oidc';
import i18next from 'i18next';
import { WhiteLabelProvider, DoxisSpinner } from '@metaforcelabs/metaforce-core';
import { I18nextProvider } from 'react-i18next';
import { RecoilRoot } from 'recoil';
// webpack 4/Jest 26 (react-scripts 4) don't resolve package.json "exports" maps, so the
// ./react subpath other apps import must be reached via its real file path here instead.
import { RenewTokenOnCompanyChange } from '@metaforcelabs/docgen-identity-web/src/react/index.js';

import { OidcRoutesContext, CurrentDataContext } from './contexts';

import { useLicense } from './hooks/useLicense';
import { useFeature } from './hooks/useFeature';
import { useIsSppUser } from './hooks/useIsSppUser';
import { useToastAction } from './hooks/useToastAction';

import { setAuthHeader, setRenewTokens } from './api/api';
import { getCustomerInfo } from './api/customer';
import { getUserProfile } from './api/userProfile';


import AdminLayout from './layouts/AdminLayout';
import ErrorLayout from './layouts/ErrorLayout';

import {InteractAdminAppContextProvider} from './contexts/providers/interactAdminAppContextProvider.js'

import Dashboardv2 from './screens/Admin/Dashboard/dashboardv2.js';

import TemplatesServer from './screens/Admin/Environments/templateServer';
import TemplatesServerTroubleShoot from './screens/Admin/Environments/templateServerTroubleshoot.js';
import TemplatesMaintainance from './screens/Admin/Templates/maintainance';
import TemplatesPublish from './screens/Admin/Templates/maintainanceTemplate';
import LogicFileProcessManager from './screens/Admin/Templates/logicFileProcessManager.js';
import ImportConnections from './screens/Admin/Templates/importConnections.js';

import EnvironmentsCreateDeploymentEnv from './screens/Admin/Environments/integrationCreateDeploymentEnv.js';
import EnvironmentsCreateDeploymentSrv from './screens/Admin/Environments/integrationCreateDeploymentSrv.js';
import EnvironmentsGroup from './screens/Admin/Environments/environmentGroup';

import { AttributeStoreDocumentList } from './screens/Admin/AttributeStore/documentList';
import { AttributeStoreDocument } from './screens/Admin/AttributeStore/document';
import Config from './screens/Admin/Config/config';

import ExtrasDeployments from './screens/Admin/Extras/deployments.js';
import ExtrasTemplates from './screens/Admin/Extras/templates.js';
import ExtrasEnvironments from './screens/Admin/Extras/environments.js';
import ExtrasSandbox from './screens/Admin/Extras/sandbox.js';
import ExtrasFileManager from './screens/Admin/Extras/fileManager.js';
import ExtrasFileTroubleshoot from './screens/Admin/Extras/fileTroubleshoot.js';
import ExtrasHubClients from './screens/Admin/Extras/hubClients.js';

import { MissingLicense } from './screens/Errors/MissingLicense';
import { NotFound } from './screens/Errors/NotFound';
import { Unauthorized } from './screens/Errors/Unauthorized';
import SelectCompany from './screens/SelectCompany';
import Oidc from './screens/Oidc';
import SessionLost, { AuthenticatingError } from './screens/Errors/SessionLost';
import { Toaster } from './toaster';
import common_en from './translations/en/common.json';
import home_en from './translations/en/home.json';
import overview_en from './translations/en/overview.json';
import { config, oidcConfig } from './utils/config';

import { useAutoDarkSide } from "./hooks/useDarkSide";

import Environmentsv2 from './screens/Admin/Environments/environmentsv2.js';
import EditEnvrionment from './screens/Admin/Environments/editEnvrionment';
import Distribution from './screens/Admin/Distribution';
import { featureFlags } from './utils/features.js';
import DistributionAlternatives from './screens/Admin/Distribution/distributionAlternatives.js';

// import useDarkSide from "./hooks/useDarkSide";

i18next.init({
  interpolation: {
    escapeValue: false
  },
  lng: 'en',
  resources: {
    en: {
      common: common_en,
      home: home_en,
      overview: overview_en
    }
  }
});

function App() {
  useAutoDarkSide();

  return (
    <div className="app min-h-screen bg-brand-background dark:bg-gray-800">
      <RecoilRoot>
        <Router>
          <OidcProvider
            configuration={oidcConfig}
            authenticatingComponent={Oidc}
            loadingComponent={Oidc}
            callbackSuccessComponent={Oidc}
            sessionLostComponent={SessionLost}
            authenticatingErrorComponent={AuthenticatingError}
            onLogoutFromAnotherTab={() => window.location.reload()}
          >
            <Routes>
              <Route path="/unauthorized" element={<ErrorLayout><Unauthorized /></ErrorLayout>} />
              <Route path="/missing-license" element={<ErrorLayout><MissingLicense /></ErrorLayout>} />
              <Route path="/not-found" element={<ErrorLayout><NotFound /></ErrorLayout>} />
              {/* Everything else is behind auth. ProtectedApp renders its own descendant
                  <Routes> with paths relative to this splat - react-router's documented
                  pattern for routes that used to render under a non-Route wrapper. */}
              <Route path="/*" element={<ProtectedApp />} />
            </Routes>
          </OidcProvider>
        </Router>
        <Toaster />
      </RecoilRoot>
    </div>
  );
}

function ProtectedApp() {
  const currentDataContext = useContext(CurrentDataContext);
  const onLeave = () => currentDataContext.dispatchCurrentDataEvent('', null);

  return (
    <OidcSecure>
      <SetAuthToken>
        <SetOidcRoutesContext>
          <I18nextProvider i18n={i18next}>
            <WhiteLabelProvider
              configuration={{
                centerpointApiUrl: config.centerpointBaseUrl
              }}
            >
              <InteractAdminAppContextProvider>
                <Routes>
                  <Route path="select-company" element={<SelectCompany />} />
                  <Route
                    index
                    element={
                      <AdminLayoutRoute
                        nav="home"
                        component={Dashboardv2}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />

                  {/* Mfdx */}
                  {/* Extras */}
                  <Route
                    path="extrs/deployments"
                    element={
                      <AdminLayoutRoute
                        nav="extrs_deployments"
                        subMenuNav="extrs"
                        component={ExtrasDeployments}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="extrs/templates"
                    element={
                      <AdminLayoutRoute
                        nav="extrs_templates"
                        subMenuNav="extrs"
                        component={ExtrasTemplates}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="extrs/environments"
                    element={
                      <AdminLayoutRoute
                        nav="extrs_environments"
                        subMenuNav="extrs"
                        component={ExtrasEnvironments}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="extrs/filemanager"
                    element={
                      <AdminLayoutRoute
                        nav="extrs_filemanager"
                        subMenuNav="extrs"
                        component={ExtrasFileManager}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="extrs/filetroubleshoot"
                    element={
                      <AdminLayoutRoute
                        nav="extrs_filetroubleshoot"
                        subMenuNav="extrs"
                        component={ExtrasFileTroubleshoot}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="extrs/hubclients"
                    element={
                      <AdminLayoutRoute
                        nav="extrs_hubclients"
                        subMenuNav="extrs"
                        component={ExtrasHubClients}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="extrs/sandbox"
                    element={
                      <AdminLayoutRoute
                        nav="extrs_sandbox"
                        subMenuNav="extrs"
                        component={ExtrasSandbox}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  {/* Templates */}
                  <Route
                    path="templates/update"
                    element={
                      <AdminLayoutRoute
                        subMenuNav="templates"
                        nav="templates_update"
                        component={TemplatesMaintainance}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="templates/publish/:groupId/:orderId/:command?"
                    element={
                      <AdminLayoutRoute
                        subMenuNav="templates"
                        nav="templates_publish"
                        component={TemplatesPublish}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="templates/processes/:groupId/:environmantId/:fileId"
                    element={
                      <AdminLayoutRoute
                        subMenuNav="templates"
                        nav="templates_processes"
                        component={LogicFileProcessManager}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="templates/connections/import/:groupId/:environmentId"
                    element={
                      <AdminLayoutRoute
                        subMenuNav="templates"
                        nav="templates_publish"
                        component={ImportConnections}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  {/* Kubernetes deployment */}
                  <Route
                    path="environments/deployment/env/:envGrpId"
                    element={
                      <AdminLayoutRoute
                        nav="environments_createdeploymentenv"
                        subMenuNav="environments"
                        component={EnvironmentsCreateDeploymentEnv}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="environments/deployment/srv/:settingsId/:update/:serverId?"
                    element={
                      <AdminLayoutRoute
                        nav="environments_v2"
                        subMenuNav="environments_v2"
                        component={EnvironmentsCreateDeploymentSrv}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  {/* Enviroments */}
                  <Route
                    path="environments/group/:settingsId"
                    element={
                      <AdminLayoutRoute
                        nav="environments_v2"
                        subMenuNav="environments_v2"
                        component={EnvironmentsGroup}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="templates/server/:envId/:settingsId"
                    element={
                      <AdminLayoutRoute
                        subMenuNav="environments_v2"
                        nav="environments_v2"
                        component={TemplatesServer}
                        featureEnabled={true}
                      />
                    }
                  />
                  <Route
                    path="templates/server/tbs/:envId/:settingsId"
                    element={
                      <AdminLayoutRoute
                        subMenuNav="environments_v2"
                        nav="environments_v2"
                        component={TemplatesServerTroubleShoot}
                        featureEnabled={true}
                      />
                    }
                  />
                  <Route
                    path="environments/v2"
                    element={
                      <AdminLayoutRoute
                        nav="environments_v2"
                        subMenuNav="environments_v2"
                        component={Environmentsv2}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="environments/v2/:groupId/:environmentId"
                    element={
                      <AdminLayoutRoute
                        nav="environments_v2"
                        subMenuNav="environments_v2"
                        component={EditEnvrionment}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  {/* Distribution */}
                  <Route
                    path="distribution"
                    element={
                      <AdminLayoutRoute
                        nav="distribution"
                        subMenuNav="distribution"
                        component={Distribution}
                        featureEnabled={featureFlags.distribution}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="distribution/:id"
                    element={
                      <AdminLayoutRoute
                        nav="distribution"
                        subMenuNav="distribution"
                        component={DistributionAlternatives}
                        featureEnabled={featureFlags.distribution}
                        onLeave={onLeave}
                      />
                    }
                  />
                  {/* Attribute Store */}
                  <Route
                    path="admin/attributestore/:environment"
                    element={
                      <AdminLayoutRoute
                        nav="attributestore_documents"
                        subMenuNav="attributestore"
                        component={AttributeStoreDocumentList}
                        requireSppUser={true}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  <Route
                    path="admin/attributestoredoc/:documentId/:environment"
                    element={
                      <AdminLayoutRoute
                        nav="attributestore_document"
                        subMenuNav="attributestore"
                        component={AttributeStoreDocument}
                        requireSppUser={true}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                  {/* Config */}
                  <Route
                    path="admin/config"
                    element={
                      <AdminLayoutRoute
                        nav="config"
                        subMenuNav="config"
                        component={Config}
                        featureEnabled={true}
                        onLeave={onLeave}
                      />
                    }
                  />
                </Routes>
              </InteractAdminAppContextProvider>
            </WhiteLabelProvider>
          </I18nextProvider>
        </SetOidcRoutesContext>
      </SetAuthToken>
    </OidcSecure>
  );
}

//#region Contexts

//#region CurrentDataContext


//#endregion

//#endregion

function AdminLayoutRoute({
  component: Component,
  featureEnabled,
  nav,
  subMenuNav,
  licenseId,
  requireSppUser,
  requiredFeatureCode,
}) {
  const { hasLicense } = useLicense();
  const { hasFeature } = useFeature();
  const { isSppUser } = useIsSppUser();

  return (
    <ProtectedRoute
      hasLicense={hasLicense(licenseId) && hasFeature(requiredFeatureCode) && (!requireSppUser || isSppUser())}
      featureEnabled={featureEnabled}
    >
      <AdminLayout nav={nav} subMenuNav={subMenuNav}>
        <Component />
      </AdminLayout>
    </ProtectedRoute>
  );
}

const ProtectedRoute = ({ featureEnabled, hasLicense, children }) => {
  return featureEnabled && hasLicense ? <>{children}</> : <Navigate to="/" replace />;
};

export const SetOidcRoutesContext = ({ children }) => {
  const loadAction = useToastAction();
  // Children stay unmounted until the first load settles, so their mount effects run once.
  const [loaded, setLoaded] = useState(false);
  const [userProfile, setUserProfile] = useState({});
  const [customerInfo, setCustomerInfo] = useState({});

  const loadUserContextData = async () => {
    await loadAction.executeAsync(async () => {
      const userProfilePromise = getUserProfile();
      const customerPromise = getCustomerInfo();
      const res = await Promise.all([userProfilePromise, customerPromise]);

      i18next.changeLanguage(res[0].languageCode);
      setUserProfile(res[0]);
      setCustomerInfo(res[1]);
    }, 'Failed to load user data');
    setLoaded(true);
  };

  useEffect(() => {
    loadUserContextData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    loaded && (
      <OidcRoutesContext.Provider
        value={{
          userProfile,
          customerInfo
        }}
      >
        {children}
      </OidcRoutesContext.Provider>
    )
  );
};

/**
 * CEN-642: wires this app's useOidc().renewTokens + setAuthHeader into the shared
 * docgen-identity-web hook, which blocks children until the mount renewal settles and
 * remounts them on a detected company switch (see @metaforcelabs/docgen-identity-web/react).
 * Also still gives api.js's 401 interceptor a way to ask the OIDC context for a fresh token,
 * independent of the hook's own renewals.
 */
export const SetAuthToken = ({ children }) => {
  const { renewTokens } = useOidc();

  useEffect(() => {
    setRenewTokens(renewTokens);
  }, [renewTokens]);

  return (
    <RenewTokenOnCompanyChange
      renewTokens={renewTokens}
      setAuthHeader={setAuthHeader}
      fallback={
        <div
          className="fixed flex flex-col items-center justify-center h-screen w-screen"
          style={{ backgroundColor: '#f5f5f5' }}
        >
          <DoxisSpinner size="lg" />
        </div>
      }
    >
      {children}
    </RenewTokenOnCompanyChange>
  );
};

export default App;
